import React, { useState, useEffect } from "react";
import { Form, Select, InputNumber, Input, Typography } from "antd";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { elvis } from "../../Utils";

const { Option } = Select;
const { Title } = Typography;

const CropForm = props => {
  const [categoryTypes, setCategoryTypes] = useState([]);
  const [growthTypes, setGrowthTypes] = useState([]);
  const [durationTypes, setDurationTypes] = useState([]);
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const isEditMode = props.isEditMode || false;

  const formItemLayout = {
    labelCol: {
      sm: { span: 8 }
    },
    wrapperCol: {
      sm: { span: 16 }
    }
  };

  // Load all types from the server
  useEffect(() => {
    axios
      .get(process.env.REACT_APP_API_ENDPOINT + "/farm/enums/crop-categories")
      .then(({ data }) => {
        setCategoryTypes(data);
      })
      .catch(function (error) {
        console.log(error);
      });

    axios
      .get(process.env.REACT_APP_API_ENDPOINT + "/farm/enums/crop-growth-stages")
      .then(({ data }) => {
        setGrowthTypes(data);
      })
      .catch(function (error) {
        console.log(error);
      });

    axios
      .get(process.env.REACT_APP_API_ENDPOINT + "/farm/enums/crop-durations")
      .then(({ data }) => {
        setDurationTypes(data);
      })
      .catch(function (error) {
        console.log(error);
      });
  }, []);

  // Initialize form with record data
  useEffect(() => {
    if (props.record.asset) {
      form.setFieldsValue({
        gatePrice: props.record.asset.gatePrice,
        averageInvested: props.record.asset.averageInvested,
        expectedYield: props.record.asset.expectedYield,
        yearProduction: props.record.asset.yearProduction,
        areaPlanted: props.record.asset.areaPlanted,
        category: props.record.asset.category,
        growth: props.record.asset.growth,
        duration: props.record.asset.duration
      });
    }
  }, [props.record.asset, form]);

  // Reset form when exiting edit mode
  useEffect(() => {
    if (!isEditMode && props.record.asset) {
      form.resetFields();
      form.setFieldsValue({
        gatePrice: props.record.asset.gatePrice,
        averageInvested: props.record.asset.averageInvested,
        expectedYield: props.record.asset.expectedYield,
        yearProduction: props.record.asset.yearProduction,
        areaPlanted: props.record.asset.areaPlanted,
        category: props.record.asset.category,
        growth: props.record.asset.growth,
        duration: props.record.asset.duration
      });
    }
  }, [isEditMode, form, props.record.asset]);

  // Handle form changes and notify parent
  const handleFormChange = () => {
    if (!isEditMode) return;

    const values = form.getFieldsValue();
    const errors = form.getFieldsError();
    const isValid = errors.every(error => !error.errors.length);

    const newAsset = { ...props.record };
    newAsset.asset.gatePrice = elvis(values.gatePrice);
    newAsset.asset.averageInvested = elvis(values.averageInvested);
    newAsset.asset.expectedYield = elvis(values.expectedYield);
    newAsset.asset.yearProduction = elvis(values.yearProduction);
    newAsset.asset.areaPlanted = elvis(values.areaPlanted);
    newAsset.asset.category = elvis(values.category);
    newAsset.asset.growth = elvis(values.growth);
    newAsset.asset.duration = elvis(values.duration);
    newAsset.valid = isValid;
    props.parentCallback(newAsset);
  };

  return (
    <Form
      form={form}
      {...formItemLayout}
      className="details_form"
      onValuesChange={handleFormChange}
      onFieldsChange={handleFormChange}
    >
      <Title
        className="details_form_header"
        title="Asset details"
        subTitle="Crop"
      />
      <Form.Item
        label="Duration"
        name="duration"
        rules={isEditMode ? [{ required: false }] : []}
      >
        <Select disabled={!isEditMode}>
          {durationTypes.map(name => (
            <Option key={name} value={name}>
              {t("farmEnums.crop_duration." + name, name)}
            </Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        label="Year production (kg)"
        name="yearProduction"
        rules={isEditMode ? [{ required: false }] : []}
      >
        <InputNumber disabled={!isEditMode} />
      </Form.Item>
      <Form.Item
        label="Expected yield (€)"
        name="expectedYield"
        rules={isEditMode ? [{ required: false }] : []}
      >
        <Input disabled={!isEditMode} />
      </Form.Item>
      <Form.Item
        label="Average invested (€)"
        name="averageInvested"
        rules={isEditMode ? [{ required: false }] : []}
      >
        <Input disabled={!isEditMode} />
      </Form.Item>
      <Form.Item
        label="Pre-disaster value (€)"
        name="gatePrice"
        rules={isEditMode ? [{ required: false }] : []}
      >
        <InputNumber disabled={!isEditMode} />
      </Form.Item>
      <Form.Item
        label="Area planted (ha)"
        name="areaPlanted"
        rules={isEditMode ? [{ required: false }] : []}
      >
        <InputNumber disabled={!isEditMode} />
      </Form.Item>
      <Form.Item
        label="Category"
        name="category"
        rules={isEditMode ? [{ required: false }] : []}
      >
        <Select disabled={!isEditMode}>
          {categoryTypes.map(name => (
            <Option key={name} value={name}>
              {t("farmEnums.crop_category." + name, name)}
            </Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        label="Growth"
        name="growth"
        rules={isEditMode ? [{ required: false }] : []}
      >
        <Select disabled={!isEditMode}>
          {growthTypes.map(name => (
            <Option key={name} value={name}>
              {t("farmEnums.crop_growth_stage." + name, name)}
            </Option>
          ))}
        </Select>
      </Form.Item>
    </Form>
  );
};

export default CropForm;